import { useAccent } from "./accent";

interface HotspotProps {
  /** Displayed number/label on the pin. */
  n: number | string;
  /** Currently selected — pin fills with the chapter accent. */
  selected?: boolean;
  /** Already explained — pin dims and stops pulsing. */
  visited?: boolean;
  onClick: () => void;
  /** Positioning classes, e.g. "absolute -right-4 -top-4". */
  className?: string;
  label?: string;
}

/**
 * Numbered pulsing pin — the shared interaction language for the SERP
 * explorer, product-page annotator, and GSC callouts.
 */
export function Hotspot({
  n,
  selected = false,
  visited = false,
  onClick,
  className = "",
  label,
}: HotspotProps) {
  const [from, to] = useAccent();
  return (
    <button
      type="button"
      aria-label={label ?? `Hotspot ${n}`}
      onClick={onClick}
      className={`z-10 flex h-10 w-10 items-center justify-center rounded-full font-display text-xl font-bold transition-transform duration-200 hover:scale-110 ${className}`}
      style={{
        // Visited pins must stay readable over BOTH the dark deck and light
        // page mocks: solid ink pill + accent ring instead of translucent white.
        background: selected
          ? `linear-gradient(135deg, ${from}, ${to})`
          : visited
            ? "rgba(11,14,26,0.92)"
            : "rgba(255,255,255,0.92)",
        color: selected ? "#05060b" : visited ? "rgba(255,255,255,0.92)" : "#05060b",
        boxShadow: selected
          ? `0 0 20px ${from}88`
          : visited
            ? `0 0 0 2px ${from}aa, 0 2px 10px rgba(0,0,0,0.35)`
            : "0 2px 12px rgba(0,0,0,0.4)",
      }}
    >
      {!selected && !visited && (
        <span className="absolute inset-0 animate-ping rounded-full bg-white/30" />
      )}
      <span className="relative">{n}</span>
    </button>
  );
}
